/* istanbul ignore file */
import { computed } from 'vue';
import { useAvailability } from '@nuxt-common/media-card-large/app/composables/useAvailability';
import { useType } from '~/modules/type/composables/useType';
import useColor from '~/modules/color/composables/useColor';
import useSize from '~/modules/size/composables/useSize';
import { useProductFlags } from '~/modules/featureControl/composables/useProductFlags';
import useUpc from '~/modules/upc/composables/useUpc';
import { useProduct } from '~/modules/product/composables/useProduct';
import useSelectedMemberId from '~/modules/latchkey/composables/useSelectedMemberId';
import { isAnyUpcAvailable } from '../utils/utils';
import { getMemberProduct } from '~/utils/product';

function getPriceRange(priceToColors) {
  const firstPrice = priceToColors?.[0]?.price;
  const lastPriceValue = priceToColors?.[priceToColors?.length - 1];
  const lastPrice = lastPriceValue?.price;

  return `${lastPrice}-${firstPrice}`;
}

export default function useColorVariant() {
  const { isTypeDrivenThirdVariantFlag: isTypeDriven } = useProductFlags();
  const { productId, productData: product } = useProduct();
  const { selectedSizeId } = useSize();
  const { color: colorAvailability } = useAvailability(productId);
  const { traitsMap } = useUpc();
  const isSizeDriven = !isTypeDriven;
  const { selectedTypeId } = useType();
  const selectedMemberId = useSelectedMemberId();

  const { colorProps } = useColor();

  /**
   * Returns colors associated to selected type and sets the availability for selectedSize + selectedType
   */
  const typeDrivenColorFiltering = () => {
    const type = product.traits.types.typeMap[selectedTypeId.value] || { colorToSizes: {} };

    // Get color IDs relevant to the selected type
    const colorIdsForType = Object.keys(type.colorToSizes).map((id) => parseInt(id, 10)) || [];

    return {
      ...colorProps?.value,
      colors: colorProps?.value.colors
        .filter((color) => colorIdsForType.includes(color.id)) // Filter colors based on type
        .map((color) => ({
          ...color,
          available: colorAvailability.value[color.id], // mark availablity
        })),
    };
  };

  /**
   * Returns colors associated to selected size and sets the availability for selectedSize + selectedType
   */
  const sizeDrivenColorFiltering = () => {
    const size = product.traits.sizes.sizeMap[selectedSizeId.value ?? -1] || { colorToTypes: {} };

    // Get color IDs relevant to the selected size
    const colorIdsForSize = Object.keys(size.colorToTypes).map((id) => parseInt(id, 10)) || [];

    return {
      ...colorProps?.value,
      colors: colorProps?.value.colors
        .filter((color) => colorIdsForSize.includes(color.id)) // Filter colors based on size
        .map((color) => ({
          ...color,
          available: colorAvailability.value[color.id], // mark availablity
        })),
    };
  };

  const filteredColorProps = computed(() => (isTypeDriven ? typeDrivenColorFiltering() : sizeDrivenColorFiltering()));

  /**
   * Returns list of colors that are available in atleast 1 upc for the selected 2nd variant
   */
  const availableColors = computed(() => {
    const { colors } = filteredColorProps.value;
    if (isSizeDriven) {
      return colors.filter((color) => isAnyUpcAvailable(traitsMap.value, { colorId: color.id, sizeId: selectedSizeId.value }));
    }
    return colors.filter((color) => isAnyUpcAvailable(traitsMap.value, { colorId: color.id, typeId: selectedTypeId.value }));
  });

  const priceToColors = computed(() => {
    const memberPriceToColors = getMemberProduct(product, selectedMemberId.value)?.traits?.traitsMaps.priceToColors;
    const isNotPriceByColors = !memberPriceToColors || memberPriceToColors.length === 1;

    return isNotPriceByColors ? [{ colorIds: Object.keys(product.traits.colors?.colorMap || {}).map((key) => Number(key)) }] : memberPriceToColors;
  });

  const priceToColorsExp = computed(() => [
    {
      colorIds: priceToColors.value?.flatMap((priceTier) => priceTier.colorIds) ?? [],
      onSale: priceToColors.value?.every((priceTier) => priceTier.onSale) ?? false,
      priceRange: (priceToColors.value?.length > 1 && getPriceRange(priceToColors.value)) ?? '',
    },
  ]);

  return {
    filteredColorProps,
    availableColors,
    priceToColors,
    priceToColorsExp,
  };
}
